import { useEffect, useMemo, useState } from 'react';
import { useCatalogProducts } from '../../../queries/useCatalogProducts';
import { useProductCategories } from '../../../queries/useProductCategories';
import { useCatalogVitrineSettings } from '../../../queries/useCatalogVitrineSettings';
import { shouldRestrictCatalogToUserAccessWeb } from '../../../utils/catalogVisibility';
import {
  shouldShowCatalogProduct,
  userHasAnyAccess,
  userHasCatalogProductAccess,
} from '../../../utils/accessUtils';
import { filterCatalogVitrineRepresentatives } from '../../../utils/catalogEquivalenceGroup';
import {
  catalogProductHasPublishedModule,
  getPublishedCatalogCards,
} from '../../../utils/productCatalog';
import { useCategories } from '../../../queries/useCategories';
import {
  PRODUCT_VITRINE_FILTER_ALL,
  PRODUCT_VITRINE_FILTER_MINE,
} from './productVitrineFilters';

const sortUnlockedFirst = (items, user) =>
  [...items].sort((a, b) => {
    const aUnlocked = userHasCatalogProductAccess(a, user);
    const bUnlocked = userHasCatalogProductAccess(b, user);
    if (aUnlocked === bUnlocked) return 0;
    return aUnlocked ? -1 : 1;
  });

export function useProductVitrineFilters({
  user,
  enabled,
  defaultToMine = true,
}) {
  const {
    data: catalogProducts = [],
    isSuccess: isCatalogLoaded,
    isError: isCatalogError,
  } = useCatalogProducts(enabled);
  const { data: productCategories = [] } = useProductCategories();
  const { data: catalogSettings } = useCatalogVitrineSettings();
  const {
    data: allCategories,
    isSuccess: areCategoriesLoaded,
    isError: isCategoriesError,
  } = useCategories();
  const [selectedFilter, setSelectedFilter] = useState(null);

  const isLoggedIn = !!user && !user?.isAnonymous;
  const isAdmin = !!user?.isAdmin;
  const hasRealAccess = userHasAnyAccess(user);
  const restrictToUserProducts =
    shouldRestrictCatalogToUserAccessWeb(catalogSettings, user) && !isAdmin;

  const sellableProducts = useMemo(() => {
    if (!enabled) return [];

    const archivedById = {};
    catalogProducts.forEach((product) => {
      product.ownIds.forEach(({ id }) => {
        archivedById[id] = !!product.archived;
      });
    });

    // Students only see products with at least one published module (admins
    // keep modules archived until the product is ready). Admins see everything.
    const publishedCards = isAdmin ? null : getPublishedCatalogCards(allCategories);

    return filterCatalogVitrineRepresentatives(
      catalogProducts.filter(
        (product) =>
          shouldShowCatalogProduct(product, user, archivedById) &&
          (!publishedCards || catalogProductHasPublishedModule(product, publishedCards))
      ),
      catalogProducts
    );
  }, [enabled, catalogProducts, user, isAdmin, allCategories]);

  const visibleProducts = useMemo(() => {
    if (!restrictToUserProducts) return sellableProducts;
    return sellableProducts.filter((product) =>
      userHasCatalogProductAccess(product, user)
    );
  }, [sellableProducts, restrictToUserProducts, user]);

  const hasUnlockedProducts = useMemo(
    () =>
      isLoggedIn &&
      visibleProducts.some((product) => userHasCatalogProductAccess(product, user)),
    [visibleProducts, user, isLoggedIn]
  );

  const shouldDefaultToMine =
    restrictToUserProducts ||
    ((defaultToMine && !isAdmin && isLoggedIn) && hasUnlockedProducts);

  const rawEffectiveFilter =
    selectedFilter ??
    (shouldDefaultToMine ? PRODUCT_VITRINE_FILTER_MINE : PRODUCT_VITRINE_FILTER_ALL);

  // Admin vê tudo — "Meus conteúdos" não se aplica.
  const effectiveFilter =
    restrictToUserProducts && rawEffectiveFilter === PRODUCT_VITRINE_FILTER_ALL
      ? PRODUCT_VITRINE_FILTER_MINE
      : isAdmin && rawEffectiveFilter === PRODUCT_VITRINE_FILTER_MINE
        ? PRODUCT_VITRINE_FILTER_ALL
        : rawEffectiveFilter;

  // Mantém "Meus conteúdos" pré-selecionado quando o aluno tem produtos.
  // Admin e visitantes ficam em "Todos" por padrão.
  useEffect(() => {
    if (!enabled || isAdmin) return;

    if (restrictToUserProducts) {
      if (
        selectedFilter === null ||
        selectedFilter === PRODUCT_VITRINE_FILTER_ALL ||
        (selectedFilter !== PRODUCT_VITRINE_FILTER_ALL &&
          selectedFilter !== PRODUCT_VITRINE_FILTER_MINE)
      ) {
        setSelectedFilter(PRODUCT_VITRINE_FILTER_MINE);
      }
      return;
    }

    if (!defaultToMine) return;

    if (shouldDefaultToMine) {
      if (
        selectedFilter === null ||
        selectedFilter === PRODUCT_VITRINE_FILTER_ALL
      ) {
        setSelectedFilter(PRODUCT_VITRINE_FILTER_MINE);
      }
      return;
    }

    if (
      isLoggedIn &&
      !hasUnlockedProducts &&
      selectedFilter === PRODUCT_VITRINE_FILTER_MINE
    ) {
      setSelectedFilter(null);
    }
  }, [
    enabled,
    isAdmin,
    defaultToMine,
    restrictToUserProducts,
    shouldDefaultToMine,
    selectedFilter,
    isLoggedIn,
    hasUnlockedProducts,
  ]);

  // Se o admin cair em "Meus conteúdos" (estado legado), volta para "Todos".
  useEffect(() => {
    if (!enabled || !isAdmin) return;
    if (selectedFilter === PRODUCT_VITRINE_FILTER_MINE) {
      setSelectedFilter(null);
    }
  }, [enabled, isAdmin, selectedFilter]);

  const filters = useMemo(() => {
    const itemsByCategoryId = new Map();

    visibleProducts.forEach((product) => {
      if (!product.productCategoryId) return;
      if (!itemsByCategoryId.has(product.productCategoryId)) {
        itemsByCategoryId.set(product.productCategoryId, []);
      }
      itemsByCategoryId.get(product.productCategoryId).push(product);
    });

    const knownCategoryIds = new Set(productCategories.map((category) => category.id));
    const categoryFilters = productCategories
      .filter(
        (category) =>
          itemsByCategoryId.has(category.id) && knownCategoryIds.has(category.id)
      )
      .map((category) => ({
        label: category.name,
        value: category.id,
      }));

    const todosFilter = { label: 'Todos', value: PRODUCT_VITRINE_FILTER_ALL };
    const mineFilter = { label: 'Meus conteúdos', value: PRODUCT_VITRINE_FILTER_MINE };

    if (restrictToUserProducts) {
      return [mineFilter];
    }

    if (!isLoggedIn || isAdmin) {
      return [...categoryFilters, todosFilter];
    }

    if (hasUnlockedProducts) {
      return [mineFilter, ...categoryFilters, todosFilter];
    }

    // Help center: "Meus conteúdos" stays hidden until the first purchase.
    return [todosFilter, ...categoryFilters];
  }, [
    visibleProducts,
    productCategories,
    isLoggedIn,
    isAdmin,
    hasUnlockedProducts,
    restrictToUserProducts,
  ]);

  const filteredProducts = useMemo(() => {
    if (!enabled) return [];

    if (effectiveFilter === PRODUCT_VITRINE_FILTER_MINE) {
      return visibleProducts.filter((product) =>
        userHasCatalogProductAccess(product, user)
      );
    }

    if (effectiveFilter === PRODUCT_VITRINE_FILTER_ALL) {
      return restrictToUserProducts
        ? visibleProducts
        : sortUnlockedFirst(visibleProducts, user);
    }

    const categoryItems = visibleProducts.filter(
      (product) => product.productCategoryId === effectiveFilter
    );
    return restrictToUserProducts
      ? categoryItems
      : sortUnlockedFirst(categoryItems, user);
  }, [enabled, effectiveFilter, visibleProducts, user, restrictToUserProducts]);

  // Only "empty" once the catalog finished loading — avoids flashing the
  // content list while products are still being fetched.
  const isEmpty =
    !!enabled &&
    (isCatalogLoaded || isCatalogError) &&
    (areCategoriesLoaded || isCategoriesError) &&
    visibleProducts.length === 0;

  return {
    isEmpty,
    visibleProducts,
    filters,
    effectiveFilter,
    selectedFilter,
    setSelectedFilter,
    filteredProducts,
    hasUnlockedProducts,
    restrictToUserProducts,
  };
}
